import React, { useMemo } from 'react';
import type { Candidate, CandidateWorkFeedItem } from '@axiom/validation';
import {
  Badge,
  Flashy,
  Gutter,
  Grid,
  GridColumn,
  GridRow,
  Icon,
  Layout,
  LayoutItem,
  Paragraph,
  useApi,
  Well,
} from '@axiom/ui';

import { WorkFeedUtil } from '../../utils/WorkFeedUtil';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateOpportunitiesLegacyApi } from '../../api/candidate-opportunities-legacy-api';

export type WorkFeedQualificationWarningsProps = {
  candidate: Candidate;
  name?: string;
  workFeedItem: CandidateWorkFeedItem;
};

export const WorkFeedQualificationWarnings = ({
  candidate,
  name = 'PENDO_ANALYTICS',
  workFeedItem,
}: WorkFeedQualificationWarningsProps) => {
  const [{ data: candidateOpportunities }, { data: experiences }] = useApi(
    CandidateOpportunitiesLegacyApi.readOpportunitiesByCandidateId(
      candidate.id
    ),
    CandidateExperienceApi.readExperiences(candidate.id)
  );

  const qualificationWarnings = useMemo(
    () =>
      WorkFeedUtil.getQualificationWarnings(
        candidate,
        workFeedItem,
        candidateOpportunities,
        experiences
      ),
    [candidate, workFeedItem, candidateOpportunities, experiences]
  );

  return (
    <div
      data-test={name}
      data-pendo-analytics-id="expressed_interest"
      data-pendo-analytics-opportunity-id={workFeedItem.opportunity.id}
      data-pendo-analytics-qualification-warnings={qualificationWarnings.join(
        ', '
      )}
      data-pendo-analytics-phase={workFeedItem.phase}
    >
      {qualificationWarnings.length > 0 && (
        <Gutter bottom="24px">
          <Well name="QUALIFICATION_WARNINGS" background="blue">
            <Badge background="red">Review Needed</Badge>
            <Gutter bottom="16px" />
            <Paragraph>
              Before submitting your profile, confirm that your qualifications
              align with these opportunity requirements.
            </Paragraph>
            <Gutter bottom="24px" />
            <Well background="light">
              <Grid columns={2}>
                <GridRow name="WARNINGS_LIST">
                  {qualificationWarnings.map(warning => (
                    <GridColumn key={warning}>
                      <Gutter vertical="4px">
                        <Flashy color="textDefault">
                          <Layout
                            direction="horizontal"
                            horizontalGutter="8px"
                            position="left middle"
                          >
                            <LayoutItem>
                              <Flashy color="iconAlert">
                                <Layout position="center middle">
                                  <Icon name="alert-filled" />
                                </Layout>
                              </Flashy>
                            </LayoutItem>
                            <LayoutItem>
                              <Paragraph>{warning}</Paragraph>
                            </LayoutItem>
                          </Layout>
                        </Flashy>
                      </Gutter>
                    </GridColumn>
                  ))}
                </GridRow>
              </Grid>
            </Well>
          </Well>
        </Gutter>
      )}
    </div>
  );
};
